<script setup lang="ts">
import { Form, Head, Link, router, usePage } from "@inertiajs/vue3";
import {
  ChevronRightIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  UsersIcon,
} from "lucide-vue-next";
import { computed, ref, watch } from "vue";

import GroupMembersSelector from "@/components/group-members-selector.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  organizationDiscussionGroupPath,
  organizationDiscussionGroupsPath,
  organizationDiscussionThreadsPath,
} from "@/routes";
import type { MentionableGroup, MentionableUser } from "@/types";

interface Props {
  groups: MentionableGroup[];
  users: MentionableUser[];
  can_manage_groups: boolean;
}

const props = defineProps<Props>();
const page = usePage<{ auth?: { organization?: { id: number } } }>();
const organizationId = computed(() => page.props.auth?.organization?.id);

const createDialogOpen = ref(false);
const editGroup = ref<MentionableGroup | null>(null);
const deleteGroupId = ref<number | null>(null);
const selectedUserIds = ref<Set<number>>(new Set());
const memberSearch = ref("");
const selectedUserIdsList = computed({
  get: () => Array.from(selectedUserIds.value),
  set: (value: number[]) => {
    selectedUserIds.value = new Set(value);
  },
});

function openCreate() {
  if (!props.can_manage_groups) return;

  selectedUserIds.value = new Set();
  memberSearch.value = "";
  createDialogOpen.value = true;
}

function openEdit(group: MentionableGroup) {
  if (!props.can_manage_groups) return;

  selectedUserIds.value = new Set(group.members?.map((m) => m.id) ?? []);
  memberSearch.value = "";
  editGroup.value = group;
}

function handleDeleteGroup() {
  if (!props.can_manage_groups || !deleteGroupId.value || !organizationId.value) return;

  router.delete(
    organizationDiscussionGroupPath(organizationId.value, deleteGroupId.value),
    { onSuccess: () => (deleteGroupId.value = null) },
  );
}

watch(
  () => props.groups,
  () => {
    if (editGroup.value) {
      const updated = props.groups.find((g) => g.id === editGroup.value!.id);
      if (updated) editGroup.value = updated;
    }
  },
);
</script>

<template>
  <Head title="Grupos de discusión" />

  <div v-if="!organizationId" class="page-container page-section text-sm text-muted-foreground">
    Selecciona una organización para administrar grupos.
  </div>

  <div v-else class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-background p-3">
    <!-- Header -->
    <div class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <h1 class="text-lg font-semibold tracking-tight">Grupos</h1>
        <p class="mt-0.5 text-[13px] leading-snug text-muted-foreground">
          Crea grupos para mencionar equipos completos con
          <span class="font-medium text-foreground">@grupo</span>
          en los hilos de discusión. La descripción es opcional y visible en el directorio.
        </p>
      </div>
      <Button as-child variant="ghost" size="sm" class="shrink-0 text-muted-foreground">
        <Link :href="organizationDiscussionThreadsPath(organizationId)">
          Hilos
          <ChevronRightIcon class="ml-0.5 size-3.5" />
        </Link>
      </Button>
    </div>

    <!-- Create button -->
    <Button v-if="props.can_manage_groups" size="sm" variant="outline" @click="openCreate">
      <PlusIcon class="mr-1.5 size-3.5" />
      Nuevo grupo
    </Button>

    <!-- Groups list -->
    <div
      v-if="props.groups.length > 0"
      class="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded-xl border border-border/70 bg-card/95"
    >
      <div
        v-for="group in props.groups"
        :key="group.id"
        class="flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-muted/40"
      >
        <!-- Group icon -->
        <div
          class="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
        >
          <UsersIcon class="size-3.5" />
        </div>

        <!-- Name + member count -->
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2">
            <span class="truncate text-sm font-medium">{{ group.name }}</span>
            <Badge variant="secondary" class="shrink-0 text-[11px] tabular-nums">
              {{ group.member_count }}
            </Badge>
          </div>
          <p v-if="group.description" class="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {{ group.description }}
          </p>
        </div>

        <!-- Actions -->
        <div v-if="props.can_manage_groups" class="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon"
            class="size-7 text-muted-foreground"
            @click="openEdit(group)"
          >
            <PencilIcon class="size-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="size-7 text-muted-foreground hover:text-destructive"
            @click="deleteGroupId = group.id"
          >
            <TrashIcon class="size-3.5" />
          </Button>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-border/70 py-10 text-center"
    >
      <UsersIcon class="size-6 text-muted-foreground/40" />
      <p class="text-sm text-muted-foreground">
        Aún no hay grupos creados.
      </p>
    </div>
  </div>

  <!-- ── Create dialog ── -->
  <Dialog v-if="props.can_manage_groups" v-model:open="createDialogOpen">
    <DialogContent class="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Nuevo grupo</DialogTitle>
        <DialogDescription>
          Asigna un nombre y selecciona los miembros.
        </DialogDescription>
      </DialogHeader>
      <Form
        :action="organizationDiscussionGroupsPath(organizationId!)"
        method="post"
        class="space-y-4"
        #default="{ processing, errors }"
        @success="createDialogOpen = false"
      >
        <div class="space-y-1.5">
          <Label for="create-group-name">Nombre</Label>
          <Input
            id="create-group-name"
            name="discussion_group[name]"
            placeholder="ej. Comercial, Diseño…"
            required
          />
          <p
            v-if="errors['discussion_group.name'] || errors.name"
            class="text-xs text-destructive"
          >
            {{ errors["discussion_group.name"] || errors.name }}
          </p>
        </div>

        <div class="space-y-1.5">
          <Label for="create-group-description">Descripción</Label>
          <Textarea
            id="create-group-description"
            name="discussion_group[description]"
            placeholder="Qué resuelve este equipo y cuándo conviene mencionarlo."
            rows="3"
            class="min-h-20"
          />
          <p
            v-if="errors['discussion_group.description'] || errors.description"
            class="text-xs text-destructive"
          >
            {{ errors["discussion_group.description"] || errors.description }}
          </p>
        </div>

        <div class="space-y-1.5">
          <Label>Miembros</Label>
          <GroupMembersSelector
            v-model:selected-ids="selectedUserIdsList"
            v-model:search="memberSearch"
            :users="props.users"
          />
          <!-- Hidden inputs for selected users (outside filtered list so they always submit) -->
          <input
            v-for="uid in selectedUserIdsList"
            :key="`hidden-c-${uid}`"
            type="hidden"
            name="discussion_group[user_ids][]"
            :value="uid"
          >
          <p class="text-xs text-muted-foreground">
            {{ selectedUserIdsList.length }} seleccionado{{ selectedUserIdsList.length !== 1 ? "s" : "" }}
          </p>
        </div>

        <DialogFooter>
          <DialogClose as-child>
            <Button variant="ghost" type="button" size="sm">Cancelar</Button>
          </DialogClose>
          <Button type="submit" size="sm" :disabled="processing">
            {{ processing ? "Creando…" : "Crear" }}
          </Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>

  <!-- ── Edit dialog ── -->
  <Dialog
    v-if="props.can_manage_groups"
    :open="editGroup !== null"
    @update:open="(v) => { if (!v) editGroup = null }"
  >
    <DialogContent v-if="editGroup" class="sm:max-w-sm">
      <DialogHeader>
        <DialogTitle>Editar grupo</DialogTitle>
        <DialogDescription>
          Modifica el nombre o los miembros del grupo.
        </DialogDescription>
      </DialogHeader>
      <Form
        :action="organizationDiscussionGroupPath(organizationId!, editGroup.id)"
        method="put"
        class="space-y-4"
        #default="{ processing }"
        @success="editGroup = null"
      >
        <div class="space-y-1.5">
          <Label :for="`edit-group-name-${editGroup.id}`">Nombre</Label>
          <Input
            :id="`edit-group-name-${editGroup.id}`"
            name="discussion_group[name]"
            :default-value="editGroup.name"
            required
          />
        </div>

        <div class="space-y-1.5">
          <Label :for="`edit-group-description-${editGroup.id}`">Descripción</Label>
          <Textarea
            :id="`edit-group-description-${editGroup.id}`"
            name="discussion_group[description]"
            :default-value="editGroup.description || ''"
            rows="3"
            class="min-h-20"
          />
        </div>

        <div class="space-y-1.5">
          <Label>Miembros</Label>
          <GroupMembersSelector
            v-model:selected-ids="selectedUserIdsList"
            v-model:search="memberSearch"
            :users="props.users"
          />
          <input
            v-for="uid in selectedUserIdsList"
            :key="`hidden-e-${uid}`"
            type="hidden"
            name="discussion_group[user_ids][]"
            :value="uid"
          >
          <p class="text-xs text-muted-foreground">
            {{ selectedUserIdsList.length }} seleccionado{{ selectedUserIdsList.length !== 1 ? "s" : "" }}
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            type="button"
            size="sm"
            @click="editGroup = null"
          >
            Cancelar
          </Button>
          <Button type="submit" size="sm" :disabled="processing">
            {{ processing ? "Guardando…" : "Guardar" }}
          </Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>

  <!-- ── Delete confirmation ── -->
  <Dialog
    v-if="props.can_manage_groups"
    :open="deleteGroupId !== null"
    @update:open="deleteGroupId = null"
  >
    <DialogContent class="sm:max-w-xs">
      <DialogHeader>
        <DialogTitle>Eliminar grupo</DialogTitle>
        <DialogDescription>
          Esta acción no se puede deshacer.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="ghost" size="sm" @click="deleteGroupId = null">
          Cancelar
        </Button>
        <Button variant="destructive" size="sm" @click="handleDeleteGroup">
          Eliminar
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
